// Legal page strings — namespace `legal`
// Covers: Terms & Conditions, Privacy Policy, Cookie Policy & Settings.
//
// Legal sections use numbered keys (s1, s2, …) with heading + body sub-keys
// so each paragraph is one translatable unit. The template iterates a computed
// array that assembles them; this avoids tm()/rt() complexity while keeping
// paragraphs intact for translators.
//
// Brand name "GAMZOR", domain "gamzor.net", and email addresses are
// kept verbatim inside the prose — only the surrounding language is translated.
// A human legal review is expected before these go live.

export default {
  en: {
    lastUpdated: 'Last updated: {date}',
    placeholder: 'Placeholder content. Final legal copy pending review.',
    breadcrumb: 'Legal',
    onThisPage: 'On this page',
    related: 'Related pages',
    helpLink: 'Help & FAQ',

    terms: {
      title: 'Terms & Conditions',
      intro:
        'These Terms & Conditions govern your use of GAMZOR (gamzor.net). By creating an account or making a purchase, you agree to be bound by these terms.',
      s1: {
        heading: '1. Service',
        body: 'GAMZOR is a marketplace that resells digital game keys sourced from third-party suppliers. Keys are delivered electronically — there is no physical fulfillment.',
      },
      s2: {
        heading: '2. Eligibility',
        body: 'You must be at least 16 years old (or the age of majority in your jurisdiction) to use the service.',
      },
      s3: {
        heading: '3. Purchase & Delivery',
        body: 'Orders are subject to availability and supplier limits. Keys are stored in your account for 24 hours after delivery; please redeem promptly.',
      },
      s4: {
        heading: '4. Refunds',
        body: 'Unrevealed keys can be refunded to your wallet within 14 days of purchase. Once a key is revealed, refunds are generally not available.',
      },
      s5: {
        heading: '5. Account & Security',
        body: 'You are responsible for keeping your password secure. Notify us immediately of any unauthorized access.',
      },
      s6: {
        heading: '6. Limitation of Liability',
        body: 'To the maximum extent permitted by law, GAMZOR is not liable for indirect, incidental, or consequential damages.',
      },
      s7: {
        heading: '7. Changes',
        body: 'We may update these terms. Material changes will be notified by email or in-app.',
      },
      s8: {
        heading: '8. Contact',
        body: 'Questions about these terms? Visit our FAQ or contact',
      },
    },

    privacy: {
      title: 'Privacy Policy',
      intro:
        'This Privacy Policy describes how GAMZOR collects, uses, and protects your personal data when you use gamzor.net.',
      s1: {
        heading: '1. Data we collect',
        li1: 'Account data: email, password (hashed), optional profile fields.',
        li2: 'Order data: products purchased, prices, key delivery records.',
        li3: 'Payment data: handled by our payment provider — we never see your card details.',
        li4: 'Technical data: IP address, browser/user-agent (for security & rate-limiting).',
      },
      s2: {
        heading: '2. How we use it',
        li1: 'To fulfill your orders and deliver keys.',
        li2: 'To prevent fraud and enforce our Terms.',
        li3: 'To send transactional emails (verification, receipts, password reset).',
      },
      s3: {
        heading: '3. Sharing',
        body: 'We share only what\'s necessary: order details with our key supplier (Kinguin), payment data with our payment provider. We do not sell your data.',
      },
      s4: {
        heading: '4. Retention',
        body: 'Account data is kept for as long as your account exists. Order records may be retained longer for tax and accounting purposes.',
      },
      s5: {
        heading: '5. Your rights',
        body: 'Under GDPR you can request access, correction, or deletion of your personal data. Contact',
        bodyEnd: 'to exercise these rights.',
      },
      s6: {
        heading: '6. Cookies',
        body: 'See our',
        cookiePolicyLink: 'Cookie Policy',
        bodyEnd: 'for details on cookies and tracking.',
      },
    },

    cookies: {
      title: 'Cookie Policy & Settings',
      intro:
        'We use cookies and similar technologies to keep the site working, understand usage, and — only with your consent — support marketing. Cookies are grouped below; you can change your choices any time in',
      introLinkText: 'Your preferences',

      // Cookie table column headers
      table: {
        cookie: 'Cookie',
        provider: 'Provider / source',
        purpose: 'Purpose',
        type: 'Type',
        expiry: 'Expiry',
      },
      noCookies: 'No {group} cookies are currently set.',

      // Cookie type labels (used in the Type column)
      cookieType: {
        session: 'Session',
        persistent: 'Persistent',
      },

      // Cookie groups
      groups: {
        essential: {
          title: 'Essential',
          blurb: 'Required for the site to work — sign-in, security and loading store content. These cannot be turned off.',
          cookies: {
            gamer_id: {
              purpose: 'Holds your signed-in access token so the app knows who you are. HttpOnly — not readable by scripts.',
              expiry: '15 minutes',
            },
            refreshToken: {
              purpose: 'Silently renews your session so you stay signed in without re-entering your password. HttpOnly.',
              expiry: '30 days',
            },
            cfuvid: {
              purpose: 'Set by Cloudflare when product images load from Kinguin\'s CDN; used for bot and rate-limit protection. Not used for tracking.',
              expiry: 'When the browser closes',
            },
          },
        },
        analytics: {
          title: 'Analytics',
          blurb: 'Help us understand how the site is used — page views, errors, session health. Optional.',
        },
        marketing: {
          title: 'Marketing',
          blurb: 'Personalised advertising and retargeting (e.g. Google, Meta). Optional.',
        },
      },

      // Preferences panel
      preferences: {
        heading: 'Your preferences',
        essential: {
          label: 'Essential',
          description: 'Required for the site to work — always on.',
        },
        analytics: {
          label: 'Analytics',
          description: 'Help us understand how the site is used.',
        },
        marketing: {
          label: 'Marketing',
          description: 'Used for personalised advertising.',
        },
        saved: 'Preferences saved.',
        lastSaved: 'Last saved: {datetime}',
        notSavedYet: 'No preferences saved yet.',
        saveButton: 'Save preferences',
        storageNote: 'Preferences are stored in your browser\'s local storage under',
        storageNoteEnd: '— clearing site data will reset them.',
      },
    },
  },

  cs: {
    lastUpdated: 'Naposledy aktualizováno: {date}',
    placeholder: 'Zástupný obsah. Finální právní text čeká na přezkoumání.',
    breadcrumb: 'Právní informace',
    onThisPage: 'Na této stránce',
    related: 'Související stránky',
    helpLink: 'Nápověda a FAQ',

    terms: {
      title: 'Obchodní podmínky',
      intro:
        'Tyto Obchodní podmínky upravují vaše používání platformy GAMZOR (gamzor.net). Vytvořením účtu nebo provedením nákupu souhlasíte s jejich dodržováním.',
      s1: {
        heading: '1. Služba',
        body: 'GAMZOR je tržiště, které prodává digitální klíče ke hrám od třetích dodavatelů. Klíče jsou doručovány elektronicky — fyzické plnění není poskytováno.',
      },
      s2: {
        heading: '2. Způsobilost',
        body: 'Pro využívání služby musíte být starší 16 let (nebo věku plnoletosti ve vašem právním řádu).',
      },
      s3: {
        heading: '3. Nákup a doručení',
        body: 'Objednávky podléhají dostupnosti zboží a limitům dodavatelů. Klíče jsou uloženy ve vašem účtu po dobu 24 hodin od doručení; uplatněte je prosím co nejdříve.',
      },
      s4: {
        heading: '4. Reklamace a vrácení',
        body: 'Neodhalené klíče lze vrátit do vaší peněženky do 14 dnů od nákupu. Jakmile je klíč zobrazen, vrácení peněz zpravidla není možné.',
      },
      s5: {
        heading: '5. Účet a bezpečnost',
        body: 'Nesete odpovědnost za zabezpečení svého hesla. Jakýkoliv neoprávněný přístup nám ihned oznamte.',
      },
      s6: {
        heading: '6. Omezení odpovědnosti',
        body: 'V maximálním rozsahu povoleném zákonem nenese GAMZOR odpovědnost za nepřímé, náhodné ani následné škody.',
      },
      s7: {
        heading: '7. Změny',
        body: 'Tyto podmínky můžeme aktualizovat. O podstatných změnách vás informujeme e-mailem nebo prostřednictvím aplikace.',
      },
      s8: {
        heading: '8. Kontakt',
        body: 'Máte dotazy ohledně těchto podmínek? Navštivte naše FAQ nebo kontaktujte',
      },
    },

    privacy: {
      title: 'Zásady ochrany osobních údajů',
      intro:
        'Tyto Zásady ochrany osobních údajů popisují, jak GAMZOR shromažďuje, využívá a chrání vaše osobní údaje při používání gamzor.net.',
      s1: {
        heading: '1. Údaje, které shromažďujeme',
        li1: 'Údaje o účtu: e-mail, heslo (hashované), volitelné profilové údaje.',
        li2: 'Údaje o objednávkách: zakoupené produkty, ceny, záznamy o doručení klíčů.',
        li3: 'Platební údaje: zpracovává náš platební poskytovatel — číslo karty nikdy nevidíme.',
        li4: 'Technické údaje: IP adresa, prohlížeč/user-agent (pro bezpečnost a omezení počtu požadavků).',
      },
      s2: {
        heading: '2. Jak údaje využíváme',
        li1: 'K plnění vašich objednávek a doručování klíčů.',
        li2: 'K prevenci podvodů a vymáhání našich podmínek.',
        li3: 'K zasílání transakčních e-mailů (ověření, potvrzení, reset hesla).',
      },
      s3: {
        heading: '3. Sdílení údajů',
        body: 'Sdílíme pouze nezbytné informace: údaje o objednávce s naším dodavatelem klíčů (Kinguin), platební údaje s naším platebním poskytovatelem. Vaše data neprodáváme.',
      },
      s4: {
        heading: '4. Uchovávání údajů',
        body: 'Údaje o účtu uchováváme po dobu existence vašeho účtu. Záznamy o objednávkách mohou být uchovávány déle z daňových a účetních důvodů.',
      },
      s5: {
        heading: '5. Vaše práva',
        body: 'Na základě GDPR máte právo požádat o přístup ke svým osobním údajům, jejich opravu nebo výmaz. Kontaktujte',
        bodyEnd: 'pro uplatnění těchto práv.',
      },
      s6: {
        heading: '6. Cookies',
        body: 'Podrobnosti o cookies a sledování najdete v naší',
        cookiePolicyLink: 'Zásadách používání cookies',
        bodyEnd: '.',
      },
    },

    cookies: {
      title: 'Zásady cookies a nastavení',
      intro:
        'Používáme cookies a podobné technologie k zajištění funkčnosti webu, pochopení jeho využití a — pouze s vaším souhlasem — k podpoře marketingu. Cookies jsou níže rozděleny do skupin; své volby můžete kdykoli změnit v části',
      introLinkText: 'Vaše předvolby',

      table: {
        cookie: 'Cookie',
        provider: 'Poskytovatel / zdroj',
        purpose: 'Účel',
        type: 'Typ',
        expiry: 'Platnost',
      },
      noCookies: 'V kategorii {group} nejsou aktuálně nastaveny žádné cookies.',

      cookieType: {
        session: 'Relace',
        persistent: 'Trvalý',
      },

      groups: {
        essential: {
          title: 'Nezbytné',
          blurb: 'Nutné pro fungování webu — přihlášení, zabezpečení a načítání obsahu obchodu. Nelze je vypnout.',
          cookies: {
            gamer_id: {
              purpose: 'Uchovává váš přístupový token, aby aplikace věděla, kdo jste. HttpOnly — skripty jej nemohou číst.',
              expiry: '15 minut',
            },
            refreshToken: {
              purpose: 'Tiše obnovuje vaši relaci, abyste zůstali přihlášeni bez nutnosti opětovného zadání hesla. HttpOnly.',
              expiry: '30 dní',
            },
            cfuvid: {
              purpose: 'Nastavuje jej Cloudflare při načítání obrázků produktů z CDN Kinguin; slouží k ochraně před boty a omezení počtu požadavků. Není určen ke sledování.',
              expiry: 'Po zavření prohlížeče',
            },
          },
        },
        analytics: {
          title: 'Analytické',
          blurb: 'Pomáhají nám pochopit, jak je web využíván — zobrazení stránek, chyby, stav relací. Volitelné.',
        },
        marketing: {
          title: 'Marketingové',
          blurb: 'Personalizovaná reklama a remarketing (např. Google, Meta). Volitelné.',
        },
      },

      preferences: {
        heading: 'Vaše předvolby',
        essential: {
          label: 'Nezbytné',
          description: 'Nutné pro fungování webu — vždy zapnuto.',
        },
        analytics: {
          label: 'Analytické',
          description: 'Pomáhají nám pochopit, jak je web využíván.',
        },
        marketing: {
          label: 'Marketingové',
          description: 'Používány pro personalizovanou reklamu.',
        },
        saved: 'Předvolby uloženy.',
        lastSaved: 'Naposledy uloženo: {datetime}',
        notSavedYet: 'Zatím nejsou uloženy žádné předvolby.',
        saveButton: 'Uložit předvolby',
        storageNote: 'Předvolby jsou uloženy v místním úložišti vašeho prohlížeče pod klíčem',
        storageNoteEnd: '— vymazáním dat webu je resetujete.',
      },
    },
  },
};
